import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { adminApi } from '../api/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Users,
  Mail,
  Shield,
  UserPlus,
  Search,
  MoreHorizontal,
  Edit,
  Trash2,
  Key,
  UserX,
  UserCheck,
  ArrowLeft,
  RefreshCw,
  Send,
  DollarSign,
  HardDrive,
  Cloud,
  ExternalLink,
  Save,
  Loader2,
  AlertCircle,
  Crown,
  Gift,
  CreditCard,
  LogIn,
  Headset,
  Megaphone,
  Database,
  Pencil,
  Lock,
  X,
  Ticket,
  MessageSquare,
  FileText,
  Settings,
  Trash
} from 'lucide-react';
import { toast } from 'sonner';
import PlansManager from '../components/admin/PlansManager';
import ReferralsManager from '../components/admin/ReferralsManager';
import SupportManager from '../components/admin/SupportManager';
import BroadcastManager from '../components/admin/BroadcastManager';
import CouponManager from '../components/admin/CouponManager';
import ContactInquiriesManager from '../components/admin/ContactInquiriesManager';
import SiteReportedIssuesManager from '../components/admin/SiteReportedIssuesManager';
import IPSecurityManager from '../components/admin/IPSecurityManager';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isAdmin, loading: authLoading } = useAuth();
  const { isDarkMode } = useTheme();
  
  // Theme-aware classes
  const bgPrimary = isDarkMode ? 'bg-[#16162a]' : 'bg-gray-50';
  const bgSecondary = isDarkMode ? 'bg-[#1a1a2e]' : 'bg-white';
  const bgTertiary = isDarkMode ? 'bg-[#252540]' : 'bg-gray-100';
  const borderColor = isDarkMode ? 'border-[#2d2d44]' : 'border-gray-200';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-300' : 'text-gray-700';
  const textMuted = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // Dialog states
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [editUserOpen, setEditUserOpen] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [deleteUserOpen, setDeleteUserOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Form states
  const [newUser, setNewUser] = useState({ email: '', displayName: '', password: '', role: 'user' });
  const [newPassword, setNewPassword] = useState('');
  
  // Plans list for user plan assignment
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !isAdmin)) {
      navigate('/inbox');
    }
  }, [authLoading, isAuthenticated, isAdmin, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersData, statsData] = await Promise.all([
        adminApi.getUsers({ search: searchQuery, role: roleFilter, status: statusFilter }),
        adminApi.getStats()
      ]);
      setUsers(usersData);
      setStats(statsData);
    } catch (err) {
      console.error('Failed to fetch admin data:', err);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      // Fetch ALL active plans (including hidden ones) for admin dropdown
      const response = await axios.get(`${API}/plans/?include_inactive=false`);
      setPlans(response.data);
    } catch (err) {
      console.error('Failed to fetch plans:', err);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchData();
      fetchPlans();
    }
  }, [isAdmin, searchQuery, roleFilter, statusFilter]);

  const handleCreateUser = async () => {
    try {
      await adminApi.createUser({
        email: newUser.email,
        password: newUser.password,
        display_name: newUser.displayName
      }, newUser.role);
      toast.success('User created successfully');
      setCreateUserOpen(false);
      setNewUser({ email: '', displayName: '', password: '', role: 'user' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    try {
      await adminApi.updateUser(selectedUser.id, {
        display_name: selectedUser.display_name,
        role: selectedUser.role,
        status: selectedUser.status,
        subscription_plan_id: selectedUser.subscription_plan_id,
        subscription_plan_name: selectedUser.subscription_plan_name,
        is_vip: selectedUser.is_vip
      });
      toast.success('User updated successfully');
      setEditUserOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser || !newPassword) return;
    try {
      await adminApi.resetPassword(selectedUser.id, newPassword);
      toast.success('Password reset successfully');
      setResetPasswordOpen(false);
      setNewPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    try {
      await adminApi.deleteUser(selectedUser.id);
      toast.success('User deleted successfully');
      setDeleteUserOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleImpersonateUser = async (targetUser) => {
    try {
      // Store the current admin token before impersonating
      const currentToken = localStorage.getItem('token');
      const currentUser = localStorage.getItem('user');
      if (currentToken && currentUser) {
        localStorage.setItem('admin_token_backup', currentToken);
        localStorage.setItem('admin_user_backup', currentUser);
      }
      
      const response = await adminApi.impersonateUser(targetUser.id);
      
      // Store the impersonated user's token and mark as impersonation
      localStorage.setItem('token', response.access_token);
      localStorage.setItem('user', JSON.stringify(response.user));
      localStorage.setItem('is_impersonating', 'true');
      localStorage.setItem('impersonated_user_email', targetUser.email);
      
      toast.success(`Signed in as ${targetUser.email}`);
      
      // Navigate to inbox as the impersonated user
      window.location.href = '/inbox';
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to sign in as user');
    }
  };

  const handleSyncStorage = async () => {
    try {
      const response = await axios.post(`${API}/admin/sync-storage-limits`);
      toast.success(`Synced storage limits for ${response.data.updated_count} users`);
      fetchData(); // Refresh user list
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to sync storage limits');
    }
  };

  if (authLoading || !isAdmin) {
    return (
      <div className={`h-screen flex items-center justify-center ${bgPrimary}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6D4AFF]"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${bgPrimary} ${textPrimary}`}>
      {/* Header */}
      <header className={`${bgSecondary} border-b ${borderColor} px-6 py-4`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/inbox')}
              className={`${textMuted} hover:${textPrimary} hover:${bgTertiary}`}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className={`text-xl font-semibold ${textPrimary}`}>Admin Dashboard</h1>
              <p className={`text-sm ${textMuted}`}>Manage users and system settings</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={fetchData}
            className={`${textMuted} hover:${textPrimary} hover:${bgTertiary}`}
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </header>

      <div className="p-6 max-w-7xl mx-auto">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className={`${bgSecondary} ${borderColor}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#6D4AFF]/20 rounded-lg">
                  <Users className="w-5 h-5 text-[#6D4AFF]" />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${textPrimary}`}>{stats.total_users || 0}</p>
                  <p className={`text-xs ${textMuted}`}>Total Users</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={`${bgSecondary} ${borderColor}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500/20 rounded-lg">
                  <UserCheck className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${textPrimary}`}>{stats.active_users || 0}</p>
                  <p className={`text-xs ${textMuted}`}>Active Users</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={`${bgSecondary} ${borderColor}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <Mail className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${textPrimary}`}>{stats.total_emails || 0}</p>
                  <p className={`text-xs ${textMuted}`}>Total Emails</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={`${bgSecondary} ${borderColor}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-500/20 rounded-lg">
                  <Send className="w-5 h-5 text-yellow-500" />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${textPrimary}`}>{stats.emails_sent_today || 0}</p>
                  <p className={`text-xs ${textMuted}`}>Sent Today</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for Users, Plans, and Storage */}
        <Tabs defaultValue="users" className="space-y-6">
          <TabsList className={`${bgSecondary} border ${borderColor}`}>
            <TabsTrigger value="users" className="data-[state=active]:bg-[#6D4AFF] data-[state=active]:text-white">
              <Users className="w-4 h-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger value="plans" className="data-[state=active]:bg-[#6D4AFF] data-[state=active]:text-white">
              <DollarSign className="w-4 h-4 mr-2" />
              Pricing Plans
            </TabsTrigger>
            <TabsTrigger value="referrals" className="data-[state=active]:bg-[#6D4AFF] data-[state=active]:text-white">
              <Gift className="w-4 h-4 mr-2" />
              Referrals
            </TabsTrigger>
            <TabsTrigger value="coupons" className="data-[state=active]:bg-[#6D4AFF] data-[state=active]:text-white">
              <Ticket className="w-4 h-4 mr-2" />
              Coupons
            </TabsTrigger>
            <TabsTrigger value="support" className="data-[state=active]:bg-[#6D4AFF] data-[state=active]:text-white">
              <Headset className="w-4 h-4 mr-2" />
              Support
            </TabsTrigger>
            <TabsTrigger value="contact" className="data-[state=active]:bg-[#6D4AFF] data-[state=active]:text-white">
              <MessageSquare className="w-4 h-4 mr-2" />
              Contact
            </TabsTrigger>
            <TabsTrigger value="broadcasts" className="data-[state=active]:bg-[#6D4AFF] data-[state=active]:text-white">
              <Megaphone className="w-4 h-4 mr-2" />
              Broadcasts
            </TabsTrigger>
            <TabsTrigger value="issues" className="data-[state=active]:bg-[#6D4AFF] data-[state=active]:text-white">
              <AlertCircle className="w-4 h-4 mr-2" />
              Reported Issues
            </TabsTrigger>
            <TabsTrigger value="ip-security" className="data-[state=active]:bg-[#6D4AFF] data-[state=active]:text-white">
              <Shield className="w-4 h-4 mr-2" />
              IP Security
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            {/* User Management */}
            <Card className={`${bgSecondary} ${borderColor}`}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className={textPrimary}>User Management</CardTitle>
                <CardDescription className={textMuted}>Create and manage user accounts</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleSyncStorage}
                  variant="outline"
                  className={`${borderColor} ${textSecondary} hover:${bgTertiary}`}
                  title="Sync storage limits with subscription plans"
                >
                  <Database className="w-4 h-4 mr-2" />
                  Sync Storage
                </Button>
                <Button
                  onClick={() => setCreateUserOpen(true)}
                  className="bg-[#6D4AFF] hover:bg-[#5B3FD9]"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Create User
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4 mb-4">
              <div className="relative flex-1">
                <Search className={`absolute left-3 top-3 h-4 w-4 ${textMuted}`} />
                <Input
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`pl-10 ${bgTertiary} ${borderColor} ${textPrimary}`}
                />
              </div>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className={`w-[150px] ${bgTertiary} ${borderColor} ${textPrimary}`}>
                  <SelectValue placeholder="All Roles" />
                </SelectTrigger>
                <SelectContent className={`${bgSecondary} ${borderColor}`}>
                  <SelectItem value="all" className={textPrimary}>All Roles</SelectItem>
                  <SelectItem value="admin" className={textPrimary}>Admin</SelectItem>
                  <SelectItem value="support" className={textPrimary}>Support</SelectItem>
                  <SelectItem value="user" className={textPrimary}>User</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className={`w-[150px] ${bgTertiary} ${borderColor} ${textPrimary}`}>
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent className={`${bgSecondary} ${borderColor}`}>
                  <SelectItem value="all" className={textPrimary}>All Status</SelectItem>
                  <SelectItem value="active" className={textPrimary}>Active</SelectItem>
                  <SelectItem value="suspended" className={textPrimary}>Suspended</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Users Table */}
            <div className={`rounded-lg border ${borderColor} overflow-hidden`}>
              <Table>
                <TableHeader>
                  <TableRow className={`${borderColor} hover:bg-transparent`}>
                    <TableHead className={textMuted}>User</TableHead>
                    <TableHead className={textMuted}>Role</TableHead>
                    <TableHead className={textMuted}>Status</TableHead>
                    <TableHead className={textMuted}>Plan</TableHead>
                    <TableHead className={textMuted}>Created</TableHead>
                    <TableHead className={textMuted}>Last Login</TableHead>
                    <TableHead className={`${textMuted} text-right`}>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id} className={borderColor}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className={`font-medium ${textPrimary}`}>{u.display_name}</p>
                              {u.is_vip && (
                                <Badge className="bg-gradient-to-r from-amber-500 to-yellow-400 text-black text-xs px-1.5 py-0.5">
                                  <Crown className="w-3 h-3 mr-1" />
                                  VIP
                                </Badge>
                              )}
                            </div>
                            <p className={`text-sm ${textMuted}`}>{u.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={
                          u.role === 'admin' ? 'bg-[#6D4AFF]' : 
                          u.role === 'support' ? 'bg-emerald-600' : 
                          'bg-gray-600'
                        }>
                          {u.role}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={u.status === 'active' ? 'bg-green-600' : 'bg-red-600'}>
                          {u.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className={textSecondary}>{u.subscription_plan_name || 'No Plan'}</span>
                      </TableCell>
                      <TableCell className={textMuted}>
                        {new Date(u.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className={textMuted}>
                        {u.last_login ? new Date(u.last_login).toLocaleDateString() : 'Never'}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className={`h-8 w-8 ${textMuted}`}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className={`${bgSecondary} ${borderColor}`}>
                            <DropdownMenuItem
                              className={`${textSecondary} focus:${bgTertiary}`}
                              onClick={() => { setSelectedUser(u); setEditUserOpen(true); }}
                            >
                              <Edit className="w-4 h-4 mr-2" />
                              Edit User
                            </DropdownMenuItem>
                            {u.id !== user?.id && (
                              <DropdownMenuItem
                                className={`text-[#6D4AFF] focus:${bgTertiary}`}
                                onClick={() => handleImpersonateUser(u)}
                              >
                                <LogIn className="w-4 h-4 mr-2" />
                                Sign in as User
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              className={`${textSecondary} focus:${bgTertiary}`}
                              onClick={() => { setSelectedUser(u); setResetPasswordOpen(true); }}
                            >
                              <Key className="w-4 h-4 mr-2" />
                              Reset Password
                            </DropdownMenuItem>
                            {u.id !== user?.id && (
                              <DropdownMenuItem
                                className={`text-red-400 focus:${bgTertiary}`}
                                onClick={() => { setSelectedUser(u); setDeleteUserOpen(true); }}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete User
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
          </TabsContent>

          {/* Plans Tab */}
          <TabsContent value="plans">
            <PlansManager />
          </TabsContent>

          {/* Storage Tab */}
          {/* Referrals Tab */}
          <TabsContent value="referrals">
            <ReferralsManager />
          </TabsContent>

          {/* Coupons Tab */}
          <TabsContent value="coupons">
            <CouponManager />
          </TabsContent>

          {/* Support Tab */}
          <TabsContent value="support">
            <SupportManager />
          </TabsContent>

          {/* Contact Inquiries Tab */}
          <TabsContent value="contact">
            <ContactInquiriesManager />
          </TabsContent>

          {/* Broadcasts Tab */}
          <TabsContent value="broadcasts">
            <BroadcastManager />
          </TabsContent>

          <TabsContent value="issues">
            <SiteReportedIssuesManager />
          </TabsContent>

          <TabsContent value="ip-security">
            <IPSecurityManager />
          </TabsContent>
        </Tabs>
      </div>

      {/* Create User Dialog */}
      <Dialog open={createUserOpen} onOpenChange={setCreateUserOpen}>
        <DialogContent className={`${bgSecondary} ${borderColor} max-h-[90vh] overflow-y-auto`}>
          <DialogHeader>
            <DialogTitle className={textPrimary}>Create New User</DialogTitle>
            <DialogDescription className={textMuted}>
              Create a new @mypatriotmail.org account
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className={textSecondary}>Display Name</Label>
              <Input
                value={newUser.displayName}
                onChange={(e) => setNewUser({ ...newUser, displayName: e.target.value })}
                className={`${bgTertiary} ${borderColor} ${textPrimary}`}
                placeholder="John Doe"
              />
            </div>
            <div className="space-y-2">
              <Label className={textSecondary}>Email Address</Label>
              <Input
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value.toLowerCase() })}
                className={`${bgTertiary} ${borderColor} ${textPrimary}`}
                placeholder="user@mypatriotmail.org"
              />
            </div>
            <div className="space-y-2">
              <Label className={textSecondary}>Password</Label>
              <Input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                className={`${bgTertiary} ${borderColor} ${textPrimary}`}
                placeholder="Min 8 characters"
              />
            </div>
            <div className="space-y-2">
              <Label className={textSecondary}>Role</Label>
              <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                <SelectTrigger className={`${bgTertiary} ${borderColor} ${textPrimary}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className={`${bgSecondary} ${borderColor}`}>
                  <SelectItem value="user" className={textPrimary}>User</SelectItem>
                  <SelectItem value="support" className={textPrimary}>Support</SelectItem>
                  <SelectItem value="admin" className={textPrimary}>Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="ghost" onClick={() => setCreateUserOpen(false)} className={textMuted}>
              Cancel
            </Button>
            <Button onClick={handleCreateUser} className="bg-[#6D4AFF] hover:bg-[#5B3FD9]">
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editUserOpen} onOpenChange={setEditUserOpen}>
        <DialogContent className={`${bgSecondary} ${borderColor} max-h-[90vh] overflow-y-auto`}>
          <DialogHeader>
            <DialogTitle className={textPrimary}>Edit User</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className={textSecondary}>Display Name</Label>
                <Input
                  value={selectedUser.display_name}
                  onChange={(e) => setSelectedUser({ ...selectedUser, display_name: e.target.value })}
                  className={`${bgTertiary} ${borderColor} ${textPrimary}`}
                />
              </div>
              <div className="space-y-2">
                <Label className={textSecondary}>Role</Label>
                <Select
                  value={selectedUser.role}
                  onValueChange={(v) => setSelectedUser({ ...selectedUser, role: v })}
                >
                  <SelectTrigger className={`${bgTertiary} ${borderColor} ${textPrimary}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={`${bgSecondary} ${borderColor}`}>
                    <SelectItem value="user" className={textPrimary}>User</SelectItem>
                    <SelectItem value="support" className={textPrimary}>Support</SelectItem>
                    <SelectItem value="admin" className={textPrimary}>Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className={textSecondary}>Status</Label>
                <Select
                  value={selectedUser.status}
                  onValueChange={(v) => setSelectedUser({ ...selectedUser, status: v })}
                >
                  <SelectTrigger className={`${bgTertiary} ${borderColor} ${textPrimary}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={`${bgSecondary} ${borderColor}`}>
                    <SelectItem value="active" className={textPrimary}>Active</SelectItem>
                    <SelectItem value="suspended" className={textPrimary}>Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className={textSecondary}>Subscription Plan</Label>
                <Select
                  value={selectedUser.subscription_plan_id || 'none'}
                  onValueChange={(v) => {
                    if (v === 'none') {
                      setSelectedUser({ ...selectedUser, subscription_plan_id: null, subscription_plan_name: null });
                    } else if (v === 'lifetime_vip') {
                      // Special Lifetime VIP option - use "The Patriot" or highest plan and mark as VIP
                      const patriotPlan = plans.find(p => p.name === 'The VIP') || plans.find(p => p.name === 'The Patriot') || plans[plans.length - 1];
                      setSelectedUser({ 
                        ...selectedUser, 
                        subscription_plan_id: patriotPlan?.id || null,
                        subscription_plan_name: 'Lifetime VIP',
                        is_vip: true
                      });
                    } else {
                      const plan = plans.find(p => p.id === v);
                      setSelectedUser({ 
                        ...selectedUser, 
                        subscription_plan_id: v, 
                        subscription_plan_name: plan?.name || ''
                      });
                    }
                  }}
                >
                  <SelectTrigger className={`${bgTertiary} ${borderColor} ${textPrimary}`}>
                    <SelectValue placeholder="Select a plan" />
                  </SelectTrigger>
                  <SelectContent className={`${bgSecondary} ${borderColor}`}>
                    <SelectItem value="none" className={textPrimary}>No Plan</SelectItem>
                    <SelectItem value="lifetime_vip" className="text-amber-400 font-semibold">
                      👑 Lifetime VIP (Free Forever)
                    </SelectItem>
                    {plans.map((plan) => (
                      <SelectItem key={plan.id} value={plan.id} className={textPrimary}>
                        {plan.name} - ${plan.monthly_price}/mo
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className={`flex items-center justify-between p-4 ${bgTertiary} rounded-lg`}>
                <div className="flex items-center gap-3">
                  <Crown className="w-5 h-5 text-amber-400" />
                  <div>
                    <p className={`${textPrimary} font-medium`}>VIP Badge</p>
                    <p className={`text-sm ${textMuted}`}>Grant special VIP status to this user</p>
                  </div>
                </div>
                <Switch
                  checked={selectedUser.is_vip || false}
                  onCheckedChange={(checked) => setSelectedUser({ ...selectedUser, is_vip: checked })}
                />
              </div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="ghost" onClick={() => setEditUserOpen(false)} className={textMuted}>
              Cancel
            </Button>
            <Button onClick={handleUpdateUser} className="bg-[#6D4AFF] hover:bg-[#5B3FD9]">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordOpen} onOpenChange={setResetPasswordOpen}>
        <DialogContent className={`${bgSecondary} ${borderColor} max-h-[90vh] overflow-y-auto`}>
          <DialogHeader>
            <DialogTitle className={textPrimary}>Reset Password</DialogTitle>
            <DialogDescription className={textMuted}>
              Set a new password for {selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label className={textSecondary}>New Password</Label>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className={`mt-2 ${bgTertiary} ${borderColor} ${textPrimary}`}
              placeholder="Min 8 characters"
            />
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="ghost" onClick={() => setResetPasswordOpen(false)} className={textMuted}>
              Cancel
            </Button>
            <Button onClick={handleResetPassword} className="bg-[#6D4AFF] hover:bg-[#5B3FD9]">
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={deleteUserOpen} onOpenChange={setDeleteUserOpen}>
        <DialogContent className={`${bgSecondary} ${borderColor} max-h-[90vh] overflow-y-auto`}>
          <DialogHeader>
            <DialogTitle className={textPrimary}>Delete User</DialogTitle>
            <DialogDescription className={textMuted}>
              Are you sure you want to delete {selectedUser?.email}? This will permanently remove all their emails and data.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="ghost" onClick={() => setDeleteUserOpen(false)} className={textMuted}>
              Cancel
            </Button>
            <Button onClick={handleDeleteUser} variant="destructive">
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
